// React
import { useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
// Componentes externos
import { Button, Container, Modal, Form } from "react-bootstrap";
import { ColumnDef, Row } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquarePlus,
  faPen,
  faTrash,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
// Componentes
import CustomTable from "../../../components/CustomTable/CustomTable";
import { Loading } from "../../../components/Loading/Loading";
// Hooks
import { useGetData } from "../../../hooks/useGetData";
import { useMutationData } from "../../../hooks/useMutationData";
// Interfaces
import { EmpresaInterface } from "../../../interfaces/EmpresaInterface";
// Contexto del tema
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";
import { useBloquesContext } from "../../../context/BloquesContext";

export const EmpresasListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] =
    useState<EmpresaInterface | null>(null);
  const [nombre, setNombre] = useState("");

  const mutateEmpresa = useMutationData({
    url: "/empresa",
    reload: ["empresa"],
  });
  const queryEmpresas = useGetData<EmpresaInterface[]>({
    url: "/empresa",
    name: ["empresa"],
  });

  const columns = useMemo<ColumnDef<EmpresaInterface>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: "nombre",
        header: () => "Nombre",
        cell: (info) => info.getValue() as string,
      },
      {
        id: "estatus",
        header: "Estatus",
        accessorFn: (row) => (row.estatus === 1 ? "Activo" : "Inactivo"),
        cell: (info) => {
          const estatus = info.getValue() as string;
          const isActive = estatus === "Activo";

          return (
            <span
              className={`${
                isActive ? "text-green-700" : "text-red-700"
              } font-bold px-2 py-0.5 rounded-full text-sm bg-opacity-80 ${
                isActive ? "bg-green-200" : "bg-red-200"
              }`}
            >
              {estatus}
            </span>
          );
        },
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="flex space-x-2">
            {/* {editarEmpresaActivo && ( */}
              <Button
                title="Editar empresa"
                className={`${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-500 hover:bg-blue-600"
                } text-white`}
                onClick={() => handleEditClick(info.row.original)}
              >
                <FontAwesomeIcon icon={faPen} className="text-white" />
              </Button>
            {/* )} */}
            {info.row.original.estatus === 0 && (
              <Button
                title="Reactivar empresa"
                className={`${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white`}
                onClick={() =>
                  mutateEmpresa.mutate({
                    id: info.row.original.id,
                    data: { estatus: 1 },
                    tipo: "patch",
                  })
                }
              >
                <FontAwesomeIcon icon={faRotateLeft} className="text-white" />
              </Button>
            )}
            {info.row.original.estatus === 1 && (
              <Button
                title="Eliminar empresa"
                className={`${
                  isDarkMode
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-500 hover:bg-red-600"
                } text-white`}
                onClick={() =>
                  mutateEmpresa.mutate({
                    id: info.row.original.id,
                    data: { estatus: 0 },
                    tipo: "delete",
                  })
                }
              >
                <FontAwesomeIcon icon={faTrash} className="text-white" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, mutateEmpresa, isDarkMode]
  );

  const handleEditClick = (empresa: EmpresaInterface) => {
    setSelectedEmpresa(empresa);
    setNombre(empresa.nombre);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmpresa(null);
  };

  const handleSaveChanges = () => {
    if (!selectedEmpresa) return;

    mutateEmpresa.mutate({
      id: selectedEmpresa.id,
      data: { nombre },
      tipo: "patch",
    });
    setShowModal(false);
  };

  if (queryEmpresas.isLoading) {
    return <Loading />;
  }

  return (
    <div
      className={`p-6 min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-3xl font-semibold">Empresas</h2>
        {/* {crearEmpresaActivo && ( */}
          <Button
            title="Crear empresa"
            className="bg-blue-500 hover:bg-blue-600 text-white"
            onClick={() => navigate("/empresas/crear")}
          >
            <FontAwesomeIcon icon={faSquarePlus} className="mr-2 text-white" />{" "}
            Crear empresa
          </Button>
        {/* )} */}
      </div>

      {queryEmpresas.data && (
        <div
          className={`shadow-md rounded-lg p-4 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <CustomTable
            {...{
              data: queryEmpresas.data,
              columns,
              getRowProps: (row: Row<EmpresaInterface>) => ({
                className:
                  row.original.estatus === 1 ? "bg-green-100" : "bg-red-100",
              }),
            }}
          />
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formEmpresaNombre">
              <Form.Label>Nombre de la Empresa</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese el nombre de la empresa"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
